<script setup>
import { useDisplay, useTheme } from 'vuetify'
const theme = useTheme()

const nuxtApp = useNuxtApp()
const { smAndDown, smAndUp } = useDisplay()
const { getFullMonthAndDate } = useFormatters()
const props = defineProps({
    id: {
        type: String,
        default: null,
    },
    handle: {
        type: String,
        default: null,
    },
})

const { data, status, error } = await useLazyAsyncData(
    `${props.handle}-roadmap`,
    () => $fetch(`/api/product/query?id=${props.id}&what=roadmap`),
    {
        transform: (data) => {
            return {
                productRoadmap: data.product.productVersions.edges.map((edge) => edge.node),
                fetchedAt: new Date(Date.now()),
            }
        },
        getCachedData(key) {
            const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]

            if (!data) {
                return
            }

            const expirationDate = new Date(data.fetchedAt)
            expirationDate.setTime(expirationDate.getTime() + 60 * 60000) // cache set to 1 hour
            const isExpired = expirationDate.getTime() < Date.now()
            if (isExpired) {
                return
            }

            return data
        },
    }
)
watchEffect(() => {
    if (error.value) {
        throw createError({
            statusCode: 500,
            message: error.value,
            fatal: false,
        })
    }
})
</script>

<template>
    <v-row id="productversions" class="justify-center rounded">
        <v-col v-if="status === 'pending' || status === 'idle'" cols="12" sm="10"><app-loader /></v-col>
        <v-col v-else cols="12" sm="10">
            <v-row class="justify-center rounded">
                <v-col cols="12" class="mt-16">
                    <app-section-header
                        :id="`roadmap`"
                        :title="`Roadmap`"
                        :subtitle="`A look at the product journey.`"
                    />
                </v-col>
                <v-col cols="12" class="d-flex mb-16 justify-center">
                    <v-timeline
                        :density="smAndUp ? 'default' : 'compact'"
                        line-color="grey-lighten-1"
                        truncate-line="both"
                        :style="
                            smAndUp
                                ? 'grid-template-columns: minmax(0, 3fr) minmax(0, 1fr) minmax(0, 3fr); z-index: 1'
                                : undefined
                        "
                    >
                        <v-timeline-item
                            v-for="roadmap in data.productRoadmap"
                            :key="roadmap.id"
                            dot-color="secondary"
                            icon="mdi-playlist-check"
                            fill-dot
                            size="small"
                            elevation="1"
                            class="mx-auto"
                        >
                            <template v-slot:opposite>
                                <div class="text-overline">
                                    {{ getFullMonthAndDate(roadmap.createdAt) }}
                                </div>
                            </template>
                            <v-card
                                variant="flat"
                                :min-width="smAndUp ? undefined : 200"
                                :max-width="smAndUp ? undefined : 300"
                                class="pa-1 st-box-shadow"
                                :style="
                                    theme.global.name.value == 'sTDarkTheme'
                                        ? 'background-color: #161616 !important'
                                        : undefined
                                "
                            >
                                <v-card-title> {{ roadmap.title }} </v-card-title>
                                <v-card-text v-for="log in roadmap.description.split('|')" :key="log" class="pa-1">
                                    <v-row>
                                        <v-col cols="1" class="text-right">
                                            <v-icon size="small" class="text-success">mdi-check</v-icon>
                                        </v-col>
                                        <v-col cols="10">
                                            {{ log }}
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-timeline-item>
                    </v-timeline>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<style scoped></style>
